// Network detection utilities for adaptive loading strategies

interface NetworkInfo {
  isSlowConnection: boolean;
  connectionType: string;
  effectiveType: string;
}

export const getNetworkInfo = (): NetworkInfo => {
  const defaultInfo: NetworkInfo = {
    isSlowConnection: false,
    connectionType: 'unknown',
    effectiveType: '4g',
  };

  // Check if the Network Information API is available
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    
    if (connection) {
      const effectiveType = connection.effectiveType || '4g';
      const connectionType = connection.type || 'unknown';
      
      // Consider 2g and slow-2g as slow connections
      const isSlowConnection = ['slow-2g', '2g'].includes(effectiveType);
      
      return {
        isSlowConnection,
        connectionType,
        effectiveType,
      };
    }
  }

  // Fallback: Check if the user has data saver enabled
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection?.saveData) {
      return {
        ...defaultInfo,
        isSlowConnection: true,
      };
    }
  }

  return defaultInfo;
};

export const shouldPreload = (): boolean => {
  const networkInfo = getNetworkInfo();
  
  // Don't preload on slow connections
  if (networkInfo.isSlowConnection) {
    return false;
  }

  // Don't preload if data saver is enabled
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection?.saveData) {
      return false;
    }
  }

  // Check if user prefers reduced data usage
  if (window.matchMedia?.('(prefers-reduced-data: reduce)').matches) {
    return false;
  }

  return true;
};

export const getPreloadDelay = (): number => {
  const networkInfo = getNetworkInfo();
  
  // Adjust delay based on connection speed
  switch (networkInfo.effectiveType) {
    case 'slow-2g':
      return 5000; // 5 seconds delay
    case '2g':
      return 3000; // 3 seconds delay
    case '3g':
      return 2000; // 2 seconds delay
    case '4g':
    default:
      return 1000; // 1 second delay
  }
};

export const logNetworkInfo = (): void => {
  const networkInfo = getNetworkInfo();
  console.log('Network Info:', {
    ...networkInfo,
    shouldPreload: shouldPreload(),
    preloadDelay: getPreloadDelay(),
  });
}; 